/**
 * Diagnosis card component displaying detailed analysis across dimensions.
 *
 * Supports two formats:
 * - Legacy: hardcoded 5 standard dimensions (cepMessage, fluency, emotion, brandLinkage, structure)
 * - Dynamic: dimensions from a template snapshot, rendered with dimension names from config
 */

import { Card } from '@/components/ui/Card';
import type {
  Diagnosis,
  DynamicDiagnosis,
  DynamicDiagnosisDimension,
  DiagnosisSection as DiagnosisSectionType,
} from '@/types';
import type { TemplateSnapshot } from '@/types/template';

import { DiagnosisSection } from './DiagnosisSection';
import { DynamicDiagnosisDimensionCard } from './DynamicDiagnosisDimensionCard';

export interface DiagnosisCardProps {
  diagnosis: Diagnosis | DynamicDiagnosis;
  templateSnapshot?: TemplateSnapshot | null;
  className?: string;
}

/**
 * Icon components for each diagnosis dimension
 */
const icons = {
  cep: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  ),
  fluency: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
  ),
  emotion: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  ),
  brand: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
      />
    </svg>
  ),
  structure: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  ),
  // Generic icon for dynamic dimensions
  dimension: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  ),
};

/** Standard legacy dimension keys */
const LEGACY_KEYS = ['cepMessage', 'fluency', 'emotion', 'brandLinkage', 'structure'];

/** Check if diagnosis uses the standard (legacy) format */
function isLegacyDiagnosis(diagnosis: Diagnosis | DynamicDiagnosis): diagnosis is Diagnosis {
  return LEGACY_KEYS.every((key) => key in diagnosis);
}

/** Format a dimension ID into a human-readable label */
function formatDimensionLabel(dimensionId: string): string {
  // Strip common prefixes
  const name = dimensionId.startsWith('dim_') ? dimensionId.slice(4) : dimensionId;
  // Insert spaces before capitals and capitalize first letter
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

export function DiagnosisCard({
  diagnosis,
  templateSnapshot,
  className,
}: DiagnosisCardProps) {

  // Dynamic diagnosis: render dimensions from template snapshot or diagnosis keys
  if (!isLegacyDiagnosis(diagnosis)) {
    // Build dimension list from template snapshot if available, otherwise from diagnosis keys
    const dimensionEntries: { id: string; name: string; data: DynamicDiagnosisDimension }[] = [];
    const diagRecord = diagnosis as Record<string, DynamicDiagnosisDimension>;

    if (templateSnapshot?.diagnosisDimensions) {
      // Use template snapshot for ordering and names
      for (const dim of templateSnapshot.diagnosisDimensions) {
        if (!dim.enabled) continue;
        const data = diagRecord[dim.dimensionId];
        if (data) {
          dimensionEntries.push({ id: dim.dimensionId, name: dim.name, data });
        }
      }
    } else {
      // No snapshot: derive from diagnosis keys
      for (const [key, data] of Object.entries(diagRecord)) {
        if (data && typeof data === 'object' && 'facts' in data) {
          dimensionEntries.push({ id: key, name: formatDimensionLabel(key), data });
        }
      }
    }

    return (
      <Card className={className}>
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900">Diagnosis</h3>
          <p className="text-sm text-gray-500 mt-1">
            Detailed analysis across {dimensionEntries.length} creative effectiveness dimension{dimensionEntries.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="space-y-6">
          {dimensionEntries.map((entry, index) => (
            <div key={entry.id}>
              {index > 0 && <div className="border-t border-gray-100 mb-6" />}
              <DynamicDiagnosisDimensionCard
                title={entry.name}
                dimension={entry.data}
                icon={icons.dimension}
              />
            </div>
          ))}
        </div>

      </Card>
    );
  }

  // Legacy diagnosis: render hardcoded 5 dimensions
  return (
    <Card className={className}>
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900">Diagnosis</h3>
        <p className="text-sm text-gray-500 mt-1">
          Detailed analysis across five creative effectiveness dimensions
        </p>
      </div>

      {/* Diagnosis Sections */}
      <div className="space-y-6">
        {/* CEP & Message */}
        <DiagnosisSection
          title="Category Entry Points & Message"
          section={diagnosis.cepMessage}
          icon={icons.cep}
        />

        <div className="border-t border-gray-100" />

        {/* Fluency */}
        <DiagnosisSection
          title="Fluency"
          section={diagnosis.fluency}
          icon={icons.fluency}
        />

        <div className="border-t border-gray-100" />

        {/* Emotion */}
        <DiagnosisSection
          title="Emotion"
          section={diagnosis.emotion as DiagnosisSectionType}
          icon={icons.emotion}
        />

        <div className="border-t border-gray-100" />

        {/* Brand Linkage (special - has level) */}
        <DiagnosisSection
          title="Brand Linkage"
          section={diagnosis.brandLinkage}
          icon={icons.brand}
        />

        <div className="border-t border-gray-100" />

        {/* Structure */}
        <DiagnosisSection
          title="Structure"
          section={diagnosis.structure}
          icon={icons.structure}
        />
      </div>
    </Card>
  );
}
